import { ProductPlanStatus } from "../components/Member/CurrentPlan/constants";
import {
  MemberProduct,
  MemberProductLevelEnum,
  MemberProductPaymentConfig,
  PayChannel,
} from "../features/api/memberApi";

const PAY_CYCLES = {
  MONTH: "MONTH",
  YEAR: "YEAR",
  WEEK: "WEEK",
  LIFETIME: "LIFETIME",
} as const;

type PayCycle = (typeof PAY_CYCLES)[keyof typeof PAY_CYCLES];

const IAP_CHANNELS = new Set<PayChannel>([
  PayChannel.APPLE_IAP,
  PayChannel.GOOGLE_IAP,
]);

/** 是否应用内购渠道（web 端不可下单 / 不可续接） */
export const isIapChannel = (channel: string): boolean =>
  IAP_CHANNELS.has(channel as PayChannel);

/** 产品列表 `platform` 参数与渠道配置 `platforms` 的取值（EDG-552） */
export const CLIENT_PLATFORM = {
  WEB: "WEB",
  IOS: "IOS",
  ANDROID: "ANDROID",
} as const;

export type ClientPlatform =
  (typeof CLIENT_PLATFORM)[keyof typeof CLIENT_PLATFORM];

/**
 * 后端未下发 `platforms` 时（旧版后端，发布顺序不一致）的回退，与 kmember `PaymentPlatformHelper`
 * 的默认值一致：Apple 内购归 iOS，Google 内购归 Android，其余归网页。
 */
const LEGACY_CHANNEL_PLATFORM: Record<string, ClientPlatform> = {
  APPLE_IAP: CLIENT_PLATFORM.IOS,
  IAP: CLIENT_PLATFORM.IOS,
  GOOGLE_IAP: CLIENT_PLATFORM.ANDROID,
  IAB: CLIENT_PLATFORM.ANDROID,
};

/**
 * 渠道配置是否面向该平台（EDG-552）。平台归属由后端产品 `payment_configs[].platforms` 决定，
 * 前端不再按渠道名区分 App / 网页；只有后端没下发该字段时才按渠道类型回退。
 */
export const isConfigOnPlatform = (
  config: MemberProductPaymentConfig,
  platform: ClientPlatform,
): boolean => {
  if (Array.isArray(config.platforms) && config.platforms.length > 0) {
    return config.platforms.includes(platform);
  }
  const channel = (config.channel ?? "").toUpperCase();
  return (LEGACY_CHANNEL_PLATFORM[channel] ?? CLIENT_PLATFORM.WEB) === platform;
};

export type CompletePlansByType = {
  weekly?: MemberProduct;
  monthly?: MemberProduct;
  yearly?: MemberProduct;
  lifetime?: MemberProduct;
};

const hasActivePaymentConfig = (
  plan: MemberProduct,
  matchConfig: (config: MemberProductPaymentConfig) => boolean,
): boolean =>
  plan.status === ProductPlanStatus.ACTIVE &&
  !!plan.payment_configs?.some(
    (config) =>
      matchConfig(config) && config.status === ProductPlanStatus.ACTIVE,
  );

/**
 * 返回产品在该平台上可用的支付渠道（仅 ACTIVE 配置，保留后端配置顺序）。
 * 渠道显隐与顺序的唯一数据来源 —— 禁止前端硬编码套餐-渠道或渠道-平台映射。
 */
export const getAvailableChannels = (
  plan: MemberProduct,
  platform: ClientPlatform = CLIENT_PLATFORM.WEB,
): PayChannel[] => {
  if (plan.status !== ProductPlanStatus.ACTIVE) {
    return [];
  }
  return [
    ...new Set(
      (plan.payment_configs ?? [])
        .filter(
          (config) =>
            config.status === ProductPlanStatus.ACTIVE &&
            isConfigOnPlatform(config, platform),
        )
        .map((config) => config.channel as PayChannel),
    ),
  ];
};

/** 便捷判定：产品是否支持某渠道（如 hasChannel(plan, PayChannel.PAYPAL)）。 */
export const hasChannel = (plan: MemberProduct, channel: PayChannel): boolean =>
  getAvailableChannels(plan).includes(channel);

export const filterActiveMemberPlans = (
  data: MemberProduct[],
  {
    isApp,
    payChannel,
  }: {
    isApp: boolean;
    payChannel: PayChannel;
  },
): MemberProduct[] => {
  if (isApp) {
    // App 内只能经原生桥走商店内购（桥接能力限制，不是平台归属判断）
    return data.filter((plan) =>
      hasActivePaymentConfig(plan, (config) => config.channel === payChannel),
    );
  }

  return data.filter((plan) =>
    hasActivePaymentConfig(plan, (config) =>
      isConfigOnPlatform(config, CLIENT_PLATFORM.WEB),
    ),
  );
};

const findExpertPlanByCycle = (
  activePlans: MemberProduct[],
  payCycle: PayCycle,
): MemberProduct | undefined =>
  activePlans.find(
    (plan) =>
      plan.level === MemberProductLevelEnum.EXPERT &&
      plan.pay_cycle === payCycle,
  );

const sanitizePlanEquities = (plan: MemberProduct): MemberProduct => {
  if (!plan.equities) {
    return plan;
  }

  return {
    ...plan,
    equities: plan.equities.filter(
      (equity: { status?: string }) =>
        equity?.status === "OWN" || equity?.status === "",
    ),
  };
};

const VALID_PAY_CYCLES = new Set<string>(Object.values(PAY_CYCLES));

export const buildSubscriptionPlans = (
  data: MemberProduct[],
  activePlans: MemberProduct[],
  billingCycle: string,
): MemberProduct[] => {
  if (!VALID_PAY_CYCLES.has(billingCycle)) {
    return [];
  }

  const payCycle = billingCycle as PayCycle;

  const freePlan = data.find(
    (plan) => plan.level === MemberProductLevelEnum.FREE,
  );
  const completePlan = findExpertPlanByCycle(activePlans, payCycle);

  return [freePlan, completePlan]
    .filter((plan): plan is MemberProduct => !!plan)
    .map(sanitizePlanEquities);
};

const toSanitizedExpertPlan = (
  activePlans: MemberProduct[],
  payCycle: PayCycle,
): MemberProduct | undefined => {
  const plan = findExpertPlanByCycle(activePlans, payCycle);
  return plan ? sanitizePlanEquities(plan) : undefined;
};

export const resolveCompletePlansByType = (
  activePlans: MemberProduct[],
): CompletePlansByType => ({
  weekly: toSanitizedExpertPlan(activePlans, PAY_CYCLES.WEEK),
  monthly: toSanitizedExpertPlan(activePlans, PAY_CYCLES.MONTH),
  yearly: toSanitizedExpertPlan(activePlans, PAY_CYCLES.YEAR),
  lifetime: toSanitizedExpertPlan(activePlans, PAY_CYCLES.LIFETIME),
});

export const resolveSubscriptionPlansFromData = (
  data: MemberProduct[],
  billingCycle: string,
  {
    isApp,
    payChannel,
  }: {
    isApp: boolean;
    payChannel: PayChannel;
  },
): MemberProduct[] => {
  const activePlans = filterActiveMemberPlans(data, { isApp, payChannel });
  return buildSubscriptionPlans(data, activePlans, billingCycle);
};

export const resolveCompletePlansFromData = (
  data: MemberProduct[],
  {
    isApp,
    payChannel,
  }: {
    isApp: boolean;
    payChannel: PayChannel;
  },
): CompletePlansByType => {
  const activePlans = filterActiveMemberPlans(data, { isApp, payChannel });
  return resolveCompletePlansByType(activePlans);
};
